import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Currency, FormattedMessage } from '@techstyle/react-intl';
import { PromoType } from '@techstyle/react-promos';
import { useProductContext } from '../ProductContext';
import { useProductGridPricingContext } from '../ProductGridPricingContext/ProductGridPricingContext';
export var bogoPromoTypes = new Set([PromoType.BOGO, PromoType.BOGO_WITH_DISCOUNT]);
var PromoPrice = styled(Currency).withConfig({
  displayName: "ProductPrice__PromoPrice",
  componentId: "sc-eskgbb-0"
})(["\n  color: ", ";\n  font-size: ", "em;\n  font-weight: bold;\n\n  ", ";\n"], _ref => {
  var theme = _ref.theme;
  return theme.colors.promoPrice;
}, 16 / 14, _ref2 => {
  var $promoPriceStyle = _ref2.$promoPriceStyle;
  return $promoPriceStyle;
});
var Price = styled(Currency).withConfig({
  displayName: "ProductPrice__Price",
  componentId: "sc-eskgbb-1"
})(["\n  ", ";\n\n  ", ";\n"], _ref3 => {
  var $showStrikethrough = _ref3.$showStrikethrough,
      theme = _ref3.theme;
  return $showStrikethrough && css(["\n      color: ", ";\n      text-decoration: line-through;\n      font-weight: normal;\n    "], theme.colors.textDisabled);
}, _ref4 => {
  var $priceStyle = _ref4.$priceStyle;
  return $priceStyle;
});
var HiddenText = styled.span.withConfig({
  displayName: "ProductPrice__HiddenText",
  componentId: "sc-eskgbb-2"
})(["\n  clip: rect(1px, 1px, 1px, 1px);\n  clip-path: inset(50%);\n  height: 1px;\n  width: 1px;\n  margin: -1px;\n  overflow: hidden;\n  padding: 0;\n"]); // this determines the appropriate text for screen readers
// to read when reading the price

var PriceHiddenText = _ref5 => {
  var discountPrice = _ref5.discountPrice,
      discountGuestPrice = _ref5.discountGuestPrice,
      shouldShowVipPrice = _ref5.shouldShowVipPrice,
      shouldShowRetailPrice = _ref5.shouldShowRetailPrice,
      defaultUnitPrice = _ref5.defaultUnitPrice,
      retailUnitPrice = _ref5.retailUnitPrice,
      showStrikeThroughForRetailPrice = _ref5.showStrikeThroughForRetailPrice,
      showStrikeThroughForVipPrice = _ref5.showStrikeThroughForVipPrice;

  if (discountPrice || shouldShowVipPrice && shouldShowRetailPrice) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "site_product_grid.price_after_discount",
      defaultMessage: "Price after discount:"
    }), ' ', /*#__PURE__*/React.createElement(Currency, {
      amount: discountPrice || defaultUnitPrice
    }));
  } else if (discountGuestPrice) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "site_product_grid.price_after_discount",
      defaultMessage: "Price after discount:"
    }), ' ', /*#__PURE__*/React.createElement(Currency, {
      amount: discountGuestPrice
    }));
  } else if ( // when there is one price with a strike through
  shouldShowVipPrice && showStrikeThroughForVipPrice || shouldShowRetailPrice && showStrikeThroughForRetailPrice) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "site_product_grid.price_before_discount",
      defaultMessage: "Price before discount:"
    }), ' ', /*#__PURE__*/React.createElement(Currency, {
      amount: shouldShowVipPrice ? defaultUnitPrice : retailUnitPrice
    }));
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "site_product_grid.current_price",
    defaultMessage: "Current price:"
  }), ' ', /*#__PURE__*/React.createElement(Currency, {
    amount: shouldShowVipPrice ? defaultUnitPrice : retailUnitPrice
  }));
};

PriceHiddenText.propTypes = {
  defaultUnitPrice: PropTypes.number,
  discountGuestPrice: PropTypes.number,
  discountPrice: PropTypes.number,
  retailUnitPrice: PropTypes.number,
  shouldShowRetailPrice: PropTypes.bool.isRequired,
  shouldShowVipPrice: PropTypes.bool.isRequired,
  showStrikeThroughForRetailPrice: PropTypes.bool.isRequired,
  showStrikeThroughForVipPrice: PropTypes.bool.isRequired
};
var ProductPrice = /*#__PURE__*/React.forwardRef((_ref6, ref) => {
  var _productGridPricingCo;

  var _ref6$as = _ref6.as,
      ElementType = _ref6$as === void 0 ? 'span' : _ref6$as,
      className = _ref6.className,
      _ref6$renderHiddenTex = _ref6.renderHiddenText,
      renderHiddenText = _ref6$renderHiddenTex === void 0 ? false : _ref6$renderHiddenTex,
      _ref6$showPromoGuestP = _ref6.showPromoGuestPrice,
      showPromoGuestPrice = _ref6$showPromoGuestP === void 0 ? false : _ref6$showPromoGuestP,
      _ref6$showPromoPrice = _ref6.showPromoPrice,
      showPromoPrice = _ref6$showPromoPrice === void 0 ? true : _ref6$showPromoPrice,
      _ref6$showRetailPrice = _ref6.showRetailPrice,
      showRetailPrice = _ref6$showRetailPrice === void 0 ? false : _ref6$showRetailPrice,
      _ref6$showSalePrice = _ref6.showSalePrice,
      showSalePrice = _ref6$showSalePrice === void 0 ? false : _ref6$showSalePrice,
      _ref6$showVipPrice = _ref6.showVipPrice,
      showVipPrice = _ref6$showVipPrice === void 0 ? true : _ref6$showVipPrice,
      strikeRegularPrice = _ref6.strikeRegularPrice,
      productFromProps = _ref6.product,
      promoPriceStyle = _ref6.promoPriceStyle,
      priceStyle = _ref6.priceStyle,
      rest = _objectWithoutProperties(_ref6, ["as", "className", "renderHiddenText", "showPromoGuestPrice", "showPromoPrice", "showRetailPrice", "showSalePrice", "showVipPrice", "strikeRegularPrice", "product", "promoPriceStyle", "priceStyle"]);

  var productFromContext = useProductContext();
  var productGridPricingContext = useProductGridPricingContext();
  var product = productFromProps || productFromContext; // attempt to retrieve pricing from the productGridPricingContext
  // pricing data comes from this only, do not reference product.* for
  // pricing data as it may be out of date if the context is used as the
  // source of truth

  var productPriceInfo = productGridPricingContext === null || productGridPricingContext === void 0 ? void 0 : (_productGridPricingCo = productGridPricingContext.productGridPricing) === null || _productGridPricingCo === void 0 ? void 0 : _productGridPricingCo[product === null || product === void 0 ? void 0 : product.masterProductId];

  var _ref7 = productPriceInfo || product,
      defaultUnitPrice = _ref7.defaultUnitPrice,
      _ref7$promos = _ref7.promos,
      promos = _ref7$promos === void 0 ? {} : _ref7$promos,
      retailUnitPrice = _ref7.retailUnitPrice,
      saleUnitPrice = _ref7.saleUnitPrice;

  var promoPrice = promos.promoPrice,
      promoTypeId = promos.promoTypeId,
      promoGuestPrice = promos.promoGuestPrice;
  var shouldShowPromoPrice = showPromoPrice && Boolean(promoPrice) && !bogoPromoTypes.has(promoTypeId);
  var shouldShowSalePrice = showSalePrice && Boolean(saleUnitPrice);
  var shouldShowVipPrice = showVipPrice && !(shouldShowPromoPrice && showRetailPrice) || !shouldShowPromoPrice && !showRetailPrice && !showPromoGuestPrice;
  var shouldShowRetailPrice = showRetailPrice && !shouldShowVipPrice || showRetailPrice && !shouldShowPromoPrice;
  var discountPrice = shouldShowPromoPrice && promoPrice || shouldShowSalePrice && saleUnitPrice;
  var discountGuestPrice = showPromoGuestPrice && promoGuestPrice;
  var showStrikeThroughForVipPrice = discountPrice || strikeRegularPrice && !shouldShowRetailPrice;
  var showStrikeThroughForRetailPrice = strikeRegularPrice || shouldShowVipPrice || shouldShowPromoPrice;
  return /*#__PURE__*/React.createElement(ElementType, _extends({
    ref: ref,
    className: className
  }, rest), discountPrice ? /*#__PURE__*/React.createElement(PromoPrice, {
    amount: discountPrice,
    "data-autotag": "product-promo-price",
    $promoPriceStyle: promoPriceStyle,
    "aria-hidden": renderHiddenText
  }) : null, discountGuestPrice && /*#__PURE__*/React.createElement(PromoPrice, {
    amount: discountGuestPrice,
    "data-autotag": "product-promo-guest-price",
    $promoPriceStyle: promoPriceStyle,
    "aria-hidden": renderHiddenText
  }), shouldShowVipPrice && /*#__PURE__*/React.createElement(React.Fragment, null, discountPrice && ' ', /*#__PURE__*/React.createElement(Price, _extends({
    $showStrikethrough: showStrikeThroughForVipPrice,
    amount: defaultUnitPrice,
    "data-autotag": "product-vip-price",
    $priceStyle: priceStyle,
    "aria-hidden": renderHiddenText
  }, rest))), shouldShowRetailPrice && /*#__PURE__*/React.createElement(React.Fragment, null, (shouldShowPromoPrice || shouldShowVipPrice) && ' ', /*#__PURE__*/React.createElement(Price, _extends({
    $showStrikethrough: showStrikeThroughForRetailPrice,
    amount: retailUnitPrice,
    "data-autotag": "product-retail-price",
    $priceStyle: priceStyle,
    "aria-hidden": renderHiddenText
  }, rest))), renderHiddenText && /*#__PURE__*/React.createElement(HiddenText, {
    "data-testid": "hidden-text"
  }, /*#__PURE__*/React.createElement(PriceHiddenText, {
    discountPrice: discountPrice,
    shouldShowVipPrice: shouldShowVipPrice,
    shouldShowRetailPrice: shouldShowRetailPrice,
    defaultUnitPrice: defaultUnitPrice,
    retailUnitPrice: retailUnitPrice,
    showStrikeThroughForRetailPrice: showStrikeThroughForRetailPrice,
    showStrikeThroughForVipPrice: showStrikeThroughForVipPrice
  })));
});
ProductPrice.displayName = 'ProductPrice';
ProductPrice.propTypes = {
  /**
   * Element type to render. Defaults to a span.
   */
  as: PropTypes.elementType,
  className: PropTypes.string,
  priceStyle: PropTypes.any,

  /**
   * Product. If none is passed, it will attempt to retrieve from
   * ProductContext.
   */
  product: PropTypes.object,
  promoPriceStyle: PropTypes.any,

  /**
   * Whether or not to render hidden text so that price is better read by screen readers
   */
  renderHiddenText: PropTypes.bool,

  /**
   * Whether to show the discounted promo guest price (for Google Shopping)
   */
  showPromoGuestPrice: PropTypes.bool,

  /**
   * Whether to show the promo price
   */
  showPromoPrice: PropTypes.bool,

  /**
   * Whether to show the retail price
   */
  showRetailPrice: PropTypes.bool,

  /**
   * Whether to show the sale price (from Console)
   */
  showSalePrice: PropTypes.bool,

  /**
   * Whether to show the VIP price
   */
  showVipPrice: PropTypes.bool,

  /**
   * Whether to always render strikethrough even when not showing discounted price
   */
  strikeRegularPrice: PropTypes.bool
};
export default ProductPrice;